/**
 * Utilitário de Efeitos Sonoros usando Web Audio API.
 * Gera "beeps" premium e responsivos sem precisar de arquivos .mp3.
 */

class AudioAlerts {
  private ctx: AudioContext | null = null;

  private isMobile(): boolean {
    if (typeof window === 'undefined') return false;
    let cockpit = false;
    try {
      cockpit =
        sessionStorage.getItem('ac_cockpit_embed') === '1' ||
        sessionStorage.getItem('ac_impersonate') === 'true';
    } catch { /* noop */ }
    return (
      cockpit ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth < 900
    );
  }

  private init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  private playTone(freq: number, type: OscillatorType, duration: number, vol = 0.1) {
    if (!this.isMobile()) return;
    try {
      this.init();
      if (!this.ctx) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime);

      gain.gain.setValueAtTime(vol, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start();
      osc.stop(this.ctx.currentTime + duration);
    } catch (e) {
      console.warn('AudioContext falhou (pode requerer interacao do usuario antes)', e);
    }
  }

  /** Som grave e suave de fechadura */
  playLock() {
    if (!this.isMobile()) return;
    this.playTone(150, 'sine', 0.4, 0.2);
    setTimeout(() => this.playTone(100, 'sine', 0.6, 0.3), 100);
  }

  /** Som agudo e rápido de abertura (sucesso) */
  playUnlock() {
    if (!this.isMobile()) return;
    this.playTone(600, 'sine', 0.1, 0.1);
    setTimeout(() => this.playTone(800, 'sine', 0.3, 0.15), 100);
  }

  /** Som decrescente de logout */
  playDisconnect() {
    if (!this.isMobile()) return;
    this.playTone(300, 'triangle', 0.2, 0.1);
    setTimeout(() => this.playTone(200, 'triangle', 0.4, 0.1), 150);
  }

  /** Toca o áudio de recebimento de notificação (recebendo_notificacao.mp3) */
  playRecebendoNotificacao() {
    try {
      const audio = new Audio('/audio/recebendo_notificacao.mp3');
      audio.volume = 0.85;
      audio.play().catch(e => console.warn('[AudioAlerts] Erro ao tocar recebendo_notificacao:', e));
    } catch (e) {
      console.warn('[AudioAlerts] HTML5 Audio não disponível:', e);
    }
  }

  /** Toca o áudio de solicitação de acesso (solicitando_acesso.mp3) */
  playSolicitandoAcesso() {
    try {
      const audio = new Audio('/audio/solicitando_acesso.mp3');
      audio.volume = 0.85;
      audio.play().catch(e => console.warn('[AudioAlerts] Erro ao tocar solicitando_acesso:', e));
    } catch (e) {
      console.warn('[AudioAlerts] HTML5 Audio não disponível:', e);
    }
  }

  /** Toca o áudio de acesso acesso_autorizado (acesso_autorizado.mp3) */
  playAcessoAutorizado() {
    try {
      const audio = new Audio('/audio/acesso_autorizado.mp3');
      audio.volume = 0.85;
      audio.play().catch(e => console.warn('[AudioAlerts] Erro ao tocar acesso_autorizado:', e));
    } catch (e) {
      console.warn('[AudioAlerts] HTML5 Audio não disponível:', e);
    }
  }

  /** Libera o AudioContext e emite um tom discreto após o primeiro gesto de interação */
  async unlock(): Promise<boolean> {
    if (!this.isMobile()) return false;
    try {
      this.init();
      if (this.ctx && this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }
      this.playTone(1000, 'sine', 0.05, 0.001); // tom curto quase inaudível para destravar o contexto
      return true;
    } catch (e) {
      console.warn('[AudioAlerts] Falha ao desbloquear contexto de áudio:', e);
      return false;
    }
  }

  /** Toca um áudio customizado a partir do caminho fornecido */
  async tocarAlerta(src = '/audio/recebendo_notificacao.mp3', options?: { allowDesktop?: boolean }): Promise<boolean> {
    if (!this.isMobile() && !options?.allowDesktop) return false;
    try {
      if (this.isMobile()) {
        await this.unlock();
      }
      const audio = new Audio(src);
      audio.volume = 0.85;
      await audio.play();
      return true;
    } catch (e) {
      console.warn(`[AudioAlerts] Erro ao tocar áudio ${src}:`, e);
      return false;
    }
  }
}

export const audioAlerts = new AudioAlerts();
